// frontend/src/pages/DashboardPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FilterListIcon from '@mui/icons-material/FilterList';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import { fetchDashboardData, fetchGenres } from '../services/dashboardService';
import { DashboardData, DashboardFilters } from '../types/PPDash';

import SummaryMetrics from '../components/PostProduction/SummaryMetrics';
import FinancialTable from '../components/PostProduction/FinancialTable';
import TicketMixChart from '../components/PostProduction/TicketMixChart';
import SalesVelocityChart from '../components/PostProduction/SalesVelocityChart';
import GenreBreakdown from '../components/PostProduction/GenreBreakdown';
import MissingReports from '../components/PostProduction/MissingReports';
import SalesIntelligencePanel from '../components/PostProduction/SalesIntelligencePanel';
import AudienceBehaviorPanel from '../components/PostProduction/AudienceBehaviorPanel';

const DashboardPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [genres, setGenres] = useState<string[]>([]);

  // Filter state - default to year-to-date
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const date = new Date();
    date.setMonth(0); // January
    date.setDate(1); // 1st
    return date;
  });
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [selectedGenre, setSelectedGenre] = useState<string>('all');

  // Fetch genres on mount
  useEffect(() => {
    const loadGenres = async () => {
      const response = await fetchGenres();
      if (response.success && response.genres) {
        setGenres(response.genres);
      }
    };
    loadGenres();
  }, []);

  // Fetch dashboard data
  const loadDashboardData = async (filters?: DashboardFilters) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchDashboardData(filters);

      if (response.success && response.data) {
        setDashboardData(response.data);
      } else {
        setError(response.error || 'Failed to load dashboard data');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount with default filters
  useEffect(() => {
    const yearStart = new Date();
    yearStart.setMonth(0);
    yearStart.setDate(1);
    yearStart.setHours(0, 0, 0, 0);
    
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    
    const filters: DashboardFilters = {};
    filters.startDate = yearStart.toISOString();
    filters.endDate = now.toISOString();
    if (selectedGenre !== 'all') filters.genre = selectedGenre;

    loadDashboardData(filters);
  }, []); // Only on mount

  // Handle filter application
  const handleApplyFilters = () => {
    const filters: DashboardFilters = {};
    if (startDate) {
      const startOfDay = new Date(startDate);
      startOfDay.setHours(0, 0, 0, 0);
      filters.startDate = startOfDay.toISOString();
    }
    if (endDate) {
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      filters.endDate = endOfDay.toISOString();
    }
    if (selectedGenre !== 'all') filters.genre = selectedGenre;

    loadDashboardData(filters);
  };

  // Handle filter reset to year-to-date
  const handleResetFilters = () => {
    const yearStart = new Date();
    yearStart.setMonth(0);
    yearStart.setDate(1);
    yearStart.setHours(0, 0, 0, 0);
    
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    
    setStartDate(yearStart);
    setEndDate(now);
    setSelectedGenre('all');

    // Load data with default filters
    loadDashboardData({
      startDate: yearStart.toISOString(),
      endDate: now.toISOString()
    });
  };

  // Handle year-to-date quick filter
  const handleYearToDate = () => {
    const yearStart = new Date();
    yearStart.setMonth(0);
    yearStart.setDate(1);
    yearStart.setHours(0, 0, 0, 0);
    
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    
    setStartDate(yearStart);
    setEndDate(now);
    
    loadDashboardData({
      startDate: yearStart.toISOString(),
      endDate: now.toISOString(),
      ...(selectedGenre !== 'all' && { genre: selectedGenre })
    });
  };

  // Handle last 3 months quick filter
  const handleLast3Months = () => {
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    threeMonthsAgo.setHours(0, 0, 0, 0);
    
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    
    setStartDate(threeMonthsAgo);
    setEndDate(now);
    
    loadDashboardData({
      startDate: threeMonthsAgo.toISOString(),
      endDate: now.toISOString(),
      ...(selectedGenre !== 'all' && { genre: selectedGenre })
    });
  };

  if (loading && !dashboardData) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Performance Intelligence Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Analyze sales patterns, ticket mix, and financial performance across all shows
          </Typography>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 4, backgroundColor: 'background.paper' }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Filters
          </Typography>
          
          {/* Quick Filters */}
          <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleYearToDate}
              disabled={loading}
            >
              Year to Date
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleLast3Months}
              disabled={loading}
            >
              Last 3 Months
            </Button>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Genre</InputLabel>
                <Select
                  value={selectedGenre}
                  label="Genre"
                  onChange={(e) => setSelectedGenre(e.target.value)}
                >
                  <MenuItem value="all">All Genres</MenuItem>
                  {genres.map(genre => (
                    <MenuItem key={genre} value={genre}>{genre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<FilterListIcon />}
                  onClick={handleApplyFilters}
                  disabled={loading}
                  fullWidth
                >
                  Apply
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RestartAltIcon />}
                  onClick={handleResetFilters}
                  disabled={loading}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Dashboard Content */}
        {!loading && dashboardData && (
          <Box>
            {/* Missing Reports Warning */}
            {dashboardData.missingReports && dashboardData.missingReports.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <MissingReports missingReports={dashboardData.missingReports} />
              </Box>
            )}

            {/* Summary Metrics */}
            <Box sx={{ mb: 4 }}>
              <SummaryMetrics summary={dashboardData.summary} />
            </Box>

            {/* Financial Table */}
            <Box sx={{ mb: 4 }}>
              <FinancialTable performances={dashboardData.performances} />
            </Box>

            {/* Charts Row 1 */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12}>
                <TicketMixChart performances={dashboardData.performances} />
              </Grid>
            </Grid>

            {/* Charts Row 2 */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12}>
                <SalesVelocityChart performances={dashboardData.performances} />
              </Grid>
            </Grid>

            {/* Sales Intelligence Panel */}
            <Box sx={{ mb: 4 }}>
              <SalesIntelligencePanel performances={dashboardData.performances} />
            </Box>

            {/* Audience Behavior Panel */}
            <Box sx={{ mb: 4 }}>
              <AudienceBehaviorPanel 
                filters={{ 
                  startDate: startDate ? (() => {
                    const d = new Date(startDate);
                    d.setHours(0, 0, 0, 0);
                    return d.toISOString();
                  })() : undefined,
                  endDate: endDate ? (() => {
                    const d = new Date(endDate);
                    d.setHours(23, 59, 59, 999);
                    return d.toISOString();
                  })() : undefined,
                  genre: selectedGenre !== 'all' ? selectedGenre : undefined
                }} 
              />
            </Box>

            {/* Genre Breakdown */}
            <Box sx={{ mb: 4 }}>
              <GenreBreakdown performances={dashboardData.performances} />
            </Box>
          </Box>
        )}

        {/* No Data State */}
        {!loading && dashboardData && dashboardData.performances.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: 'background.paper' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No performances found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your filters or date range to see data
            </Typography>
          </Paper>
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default DashboardPage;